import React, { useMemo } from 'react';
import classnames from 'classnames';
import { useTranslation } from 'react-i18next';
import { PapiUser } from '@wix/da-papi-types';
import { Url } from '@wix/da-url';
import BasicUserName from '../../../User/BasicUserName';
import UserAvatar from '../../../User/Avatar';
import PinIcon from '@wix/da-ds/pkg/Icons/24x24/Pin';
import GraduationCap from '@wix/da-ds/pkg/Icons/16x16/GraduationCap';
import { grouphubId } from '../../redux/constants';
import Flex from '@wix/da-ds/pkg/Flex';
import {
  BiData,
  Networkbar3DotsOpenBiEvent,
  Networkbar3DotsSelectBiEvent,
  NetworkbarNavClickBiEvent,
  UserNameClickBiEvent,
  ViewGroupNotificationEvent,
} from '@wix/da-bi/pkg/events';
import { ResourceType } from '../../../types/resource';
import { IconSize } from '@wix/da-ds/pkg/Icons/IconWrap';
import NetworkBarItem from '../NetworkBarItem';

import s from './NetworkBarGroupLink.scss';

export interface Props {
  group: PapiUser;
  tabIndex?: number;
  rank?: number;
  isSelected?: boolean;
  isCollapsed?: boolean;
  className?: string;
  togglePinGroup: (groupId: number, isPinnedAfter: boolean) => void;
  unrecommendGroup: (groupId: number) => void;
}

const NetworkBarGroupLink: React.FC<Props> = ({
  group,
  isSelected,
  isCollapsed = false,
  tabIndex,
  rank,
  togglePinGroup,
  unrecommendGroup,
  className,
}) => {
  const { t } = useTranslation();
  const isGroupHub = group.userId === grouphubId;

  const menuItems = useMemo(() => {
    const menuItemBi = BiData<Networkbar3DotsSelectBiEvent>({
      evid: 163,
      itemid: group.userId,
      typeid: ResourceType.GROUP,
      indicator: group.networkbar?.hasNewContent ? 1 : 0,
      rank,
    });
    return [
      group.isWatching &&
        !isGroupHub &&
        (group.networkbar?.isPinned
          ? {
              label: t('networkBar.groups.group.actions.unpin'),
              onClick: () => togglePinGroup(group.userId, false),
              biData: { ...menuItemBi, menu_item: 'unpin' },
            }
          : {
              label: t('networkBar.groups.group.actions.pin'),
              onClick: () => togglePinGroup(group.userId, true),
              biData: { ...menuItemBi, menu_item: 'pin' },
            }),
      group.extendedGroup?.isAdmin && {
        label: t('networkBar.groups.group.actions.notifications'),
        link: Url.groupNotificationsLink(group.userId),
        biData: BiData<ViewGroupNotificationEvent>({
          evid: 165,
          typeid: ResourceType.GROUP,
          itemid: group.userId,
          groupid: group.useridUuid,
          sectionname: 'menu',
        }),
      },
      {
        label: t('networkBar.groups.group.actions.visit'),
        link: Url.userLink(group.username),
        biData: BiData<UserNameClickBiEvent>({
          evid: 101,
          itemid: group.userId,
          typeid: ResourceType.GROUP,
          sectionname: 'sidebar',
        }),
      },
      !group.isWatching &&
        !isGroupHub && {
          label: t('networkBar.groups.group.actions.unrecommend'),
          onClick: () => unrecommendGroup(group.userId),
          biData: { ...menuItemBi, menu_item: 'unrecommend' },
        },
    ];
  }, [
    group.isWatching,
    group.networkbar,
    group.userId,
    group.useridUuid,
    group.username,
    group.extendedGroup,
    rank,
    t,
    togglePinGroup,
    unrecommendGroup,
    isGroupHub,
  ]);

  // custom group icon for GroupHub
  const groupAvatarUser = isGroupHub
    ? {
        ...group,
        usericon: 'https://st.deviantart.net/eclipse/global/svg/grouphub.svg?1',
      }
    : group;

  const PinIconComponent = isGroupHub ? GraduationCap : PinIcon;

  const url = isGroupHub
    ? Url.userLink(group.username)
    : group.networkbar?.url || Url.browseGroupFeedLink(group.username);

  return (
    <NetworkBarItem
      aria-label={groupAvatarUser.username}
      url={url}
      icon={
        <UserAvatar
          user={groupAvatarUser}
          size={24}
          className={classnames(isGroupHub && s['grouphub'])}
          avatarClassName={classnames(isGroupHub && s['grouphub-avatar'])}
          withLink={false}
        />
      }
      tooltipContent={
        <Flex alignItems="center">
          {group.username}
          {group.networkbar?.isPinned && (
            <PinIconComponent size={IconSize.TINY} disableDefaultColors className={s['pin-icon']} />
          )}
        </Flex>
      }
      className={classnames(
        s['root'],
        isCollapsed && s['collapsed'],
        isSelected && s['selected'],
        className
      )}
      linkClassName={classnames(
        s['group-link'],
        (group.networkbar?.isPinned || group.extendedGroup?.isAdmin) && s['has-pin']
      )}
      menuClassName={s['actions']}
      iconClassName={s['avatar']}
      isCollapsed={isCollapsed}
      isSelected={isSelected}
      hasNewContent={group.networkbar?.hasNewContent}
      tabIndex={tabIndex}
      menuItems={menuItems}
      biData={BiData<NetworkbarNavClickBiEvent>({
        evid: 160,
        itemid: group.userId,
        typeid: ResourceType.GROUP,
        indicator: group.networkbar?.hasNewContent ? 1 : 0,
        rank,
        is_expanded: isCollapsed ? 0 : 1,
      })}
      menuBiData={BiData<Networkbar3DotsOpenBiEvent>({
        evid: 162,
        itemid: group.userId,
        typeid: ResourceType.GROUP,
        indicator: group.networkbar?.hasNewContent ? 1 : 0,
        rank,
      })}
    >
      {group.networkbar?.isPinned && (
        <PinIconComponent size={IconSize.SMALLER} disableDefaultColors />
      )}
      <span className={s['username']}>
        <BasicUserName user={group} showSymbol={false} />
      </span>
    </NetworkBarItem>
  );
};
NetworkBarGroupLink.displayName = 'NetworkBarGroupLink';

export default NetworkBarGroupLink;
